/**
 * Supabase Client Configuration
 *
 * This file initializes and exports the Supabase client for frontend use.
 * The client is configured using Vite environment variables for security.
 *
 * @fileoverview Supabase client initialization for authentication
 *
 * @example
 * import { supabase } from '../lib/supabase';
 *
 * // Sign in
 * const { data, error } = await supabase.auth.signInWithPassword({
 *   email: 'user@example.com',
 *   password: 'password123'
 * });
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { isE2EAuthBypassActive, buildE2EBypassSession } from './e2eAuth';

/**
 * Supabase project URL from environment variables
 * This is the public URL of your Supabase project
 */
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;

/**
 * Supabase anonymous key from environment variables
 * This is the public anon key (safe to expose in frontend)
 */
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

/**
 * Validate that required environment variables are present
 * Throws an error during development if configuration is missing
 */



if (!supabaseUrl || !supabaseAnonKey) {
  console.error(
    'Missing Supabase environment variables. Please ensure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set in your .env file.'
  );
}

/**
 * Supabase client instance
 *
 * Pre-configured client for authentication and database operations.
 * Uses the anon key which has Row Level Security (RLS) enabled.
 *
 * @example
 * // Authentication
 * const { data: { user } } = await supabase.auth.getUser();
 *
 * // Sign out
 * await supabase.auth.signOut();
 */
export const supabase: SupabaseClient = createClient(
  supabaseUrl || '',
  supabaseAnonKey || '',
  {
    auth: {
      // Automatically refresh the token before it expires
      autoRefreshToken: true,
      // Persist session in localStorage
      persistSession: true,
      // Detect session from URL (for OAuth and email confirmation)
      detectSessionInUrl: true,
    },
  }
);

// E2E bypass: AuthContext fabricates its own session for its own React
// state, but several hooks/pages call `supabase.auth.getSession()` (or
// `getUser()`) directly instead of reading AuthContext — useUserProfile,
// ConnectedBanksList, adminApi, usersApi, StatementsPage, and others. Left
// unpatched, those would see the real (empty) Supabase session in bypass
// mode and silently fail their own auth checks — e.g. AdminRoute reading
// `isAdmin: false` and redirecting away from /snapshots even though the
// backend correctly resolves an ADMIN user for the very same request.
// Patching the client itself, once, covers every current and future direct
// caller instead of threading the bypass through each one individually.
if (isE2EAuthBypassActive()) {
  const { user, session } = buildE2EBypassSession();
  supabase.auth.getSession = async () => ({ data: { session }, error: null }) as any;
  supabase.auth.getUser = async () => ({ data: { user }, error: null }) as any;
}

export default supabase;
